import { z } from 'zod';
import { IssueCollector, ReportPadError, limitExceeded } from './errors.js';
import { LIMITS } from './limits.js';
import { DatasetDraftSchema, datasetSchemaFingerprint, findColumn, isNumericColumn } from './dataset.js';
import { ReportDefinitionSchema, definitionColumnIds, widgetColumnIds } from './report.js';
import { metricRequiresNumeric } from './metrics.js';
// ---------------------------------------------------------------------------
// Dataset schema
// ---------------------------------------------------------------------------
export function validateDatasetDraft(input) {
    const parsed = DatasetDraftSchema.safeParse(input);
    if (!parsed.success) {
        throw new ReportPadError('DATASET_SCHEMA_INVALID', 'The dataset definition is not valid.', {
            issues: zodIssues(parsed.error, 'DATASET_SCHEMA_INVALID')
        });
    }
    const draft = parsed.data;
    const collector = new IssueCollector();
    const seen = new Set();
    for (const column of draft.columns) {
        if (seen.has(column.id)) {
            collector.add('DATASET_SCHEMA_INVALID', `Duplicate column id "${column.id}".`, { columnId: column.id });
        }
        seen.add(column.id);
    }
    for (const keyColumn of draft.uniqueKey ?? []) {
        if (!seen.has(keyColumn)) {
            collector.add('DATASET_SCHEMA_INVALID', `Unique key references column "${keyColumn}", which is not defined.`, { columnId: keyColumn });
        }
    }
    collector.throwIfFailed('DATASET_SCHEMA_INVALID', 'The dataset definition is not valid');
    return draft;
}
/** Type check for a single cell against its column. Returns an issue code or
 *  null when the value is acceptable. `fromCsv` drops the "send it as a JSON
 *  number" hint for the number-type-mismatch message: a CSV cell was always a
 *  string on the wire, so the advice does not apply. */
function checkCell(column, value, fromCsv = false) {
    if (value === null) {
        if (column.nullable)
            return null;
        return {
            code: 'ROW_REQUIRED_VALUE_MISSING',
            message: `Column "${column.label}" is not nullable but the value is null.`
        };
    }
    switch (column.type) {
        case 'number':
        case 'integer': {
            if (typeof value !== 'number') {
                const base = `Column "${column.label}" expects a ${column.type}, received ${typeof value} (${JSON.stringify(value)}).`;
                return {
                    code: 'ROW_TYPE_MISMATCH',
                    message: fromCsv ? base : `${base} Numbers must be sent as JSON numbers, not strings.`
                };
            }
            if (!Number.isFinite(value)) {
                return {
                    code: 'ROW_VALUE_NOT_FINITE',
                    message: `Column "${column.label}" received ${String(value)}, which is not a finite number.`
                };
            }
            if (column.type === 'integer' && !Number.isInteger(value)) {
                return {
                    code: 'ROW_TYPE_MISMATCH',
                    message: `Column "${column.label}" expects an integer, received ${value}.`
                };
            }
            return null;
        }
        case 'boolean':
            return typeof value === 'boolean'
                ? null
                : { code: 'ROW_TYPE_MISMATCH', message: `Column "${column.label}" expects true or false, received ${typeof value}.` };
        case 'string':
            return typeof value === 'string'
                ? null
                : { code: 'ROW_TYPE_MISMATCH', message: `Column "${column.label}" expects a string, received ${typeof value}.` };
        case 'date': {
            if (typeof value !== 'string') {
                return { code: 'ROW_TYPE_MISMATCH', message: `Column "${column.label}" expects a date string, received ${typeof value}.` };
            }
            if (Number.isNaN(Date.parse(value))) {
                return {
                    code: 'ROW_TYPE_MISMATCH',
                    message: `Column "${column.label}" expects a parseable date (ISO 8601 preferred), received "${value}".`
                };
            }
            return null;
        }
    }
}
/**
 * Validates submitted rows against a dataset schema.
 *
 * Reports up to `maxIssues` problems at once with row and column locations, so
 * a supplying agent can fix an entire batch in one pass instead of discovering
 * one bad cell per round trip.
 */
export function validateRows(dataset, input, options = {}) {
    const maxIssues = options.maxIssues ?? 50;
    if (!Array.isArray(input)) {
        throw new ReportPadError('INVALID_REQUEST', 'Rows must be an array of objects.');
    }
    if (input.length > LIMITS.maxSnapshotRows) {
        throw limitExceeded('Snapshot row count', input.length, LIMITS.maxSnapshotRows);
    }
    const lines = options.lines;
    const fromCsv = Boolean(lines);
    // Sentence-initial label, e.g. "Row 3" or "Line 6" (when the row came from a
    // file, `lines.numbers[index]` is the 1-based source line, read directly —
    // never recomputed as index + N).
    const rowLabel = (index) => (lines ? `${lines.label} ${lines.numbers[index]}` : `Row ${index}`);
    // Mid-sentence label. Without `lines` this preserves today's lowercase
    // "row N" wording in the duplicate-key message unchanged; with `lines` both
    // references use the same label so the two rows are named consistently.
    const priorRowLabel = (index) => (lines ? rowLabel(index) : `row ${index}`);
    const where = (index, extra = {}) => lines ? { row: index, line: lines.numbers[index], ...extra } : { row: index, ...extra };
    const collector = new IssueCollector();
    const columnsById = new Map(dataset.columns.map((c) => [c.id, c]));
    const uniqueKey = dataset.uniqueKey ?? [];
    const seenKeys = new Map();
    const rows = [];
    for (let index = 0; index < input.length; index++) {
        if (collector.issues.length >= maxIssues)
            break;
        const raw = input[index];
        if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
            collector.add('ROW_TYPE_MISMATCH', `${rowLabel(index)} is not an object.`, where(index));
            continue;
        }
        const source = raw;
        const row = {};
        for (const key of Object.keys(source)) {
            if (!columnsById.has(key)) {
                collector.add('ROW_UNKNOWN_COLUMN', `${rowLabel(index)} contains column "${key}", which is not in the dataset schema.`, where(index, { columnId: key }));
            }
        }
        for (const column of dataset.columns) {
            const present = Object.prototype.hasOwnProperty.call(source, column.id);
            if (!present) {
                if (!column.nullable) {
                    collector.add('ROW_MISSING_COLUMN', `${rowLabel(index)} is missing required column "${column.id}".`, where(index, { columnId: column.id }));
                    continue;
                }
                row[column.id] = null;
                continue;
            }
            const value = (source[column.id] ?? null);
            const problem = checkCell(column, value, fromCsv);
            if (problem) {
                collector.add(problem.code, `${rowLabel(index)}: ${problem.message}`, where(index, { columnId: column.id }));
                continue;
            }
            row[column.id] = value;
        }
        if (uniqueKey.length > 0) {
            // JSON-encoded tuple: unambiguous for any cell value, with no separator
            // character that a legitimate string could contain.
            const keyParts = uniqueKey.map((id) => JSON.stringify(row[id] ?? null));
            const key = JSON.stringify(keyParts);
            const firstIndex = seenKeys.get(key);
            if (firstIndex !== undefined) {
                const readable = uniqueKey.map((id, i) => `${id}=${keyParts[i]}`).join(', ');
                collector.add('DUPLICATE_UNIQUE_KEY', `${rowLabel(index)} repeats the declared unique key already used by ${priorRowLabel(firstIndex)} (${readable}).`, where(index));
            }
            else {
                seenKeys.set(key, index);
            }
        }
        rows.push(row);
    }
    collector.throwIfFailed('INVALID_REQUEST', `Snapshot rows failed validation`, {
        rowsSubmitted: input.length,
        issueCount: collector.issues.length,
        truncated: collector.issues.length >= maxIssues
    });
    return {
        rows,
        rowCount: rows.length,
        schemaFingerprint: datasetSchemaFingerprint(dataset)
    };
}
// ---------------------------------------------------------------------------
// Report definition
// ---------------------------------------------------------------------------
function validateMetricAgainstDataset(metric, dataset, collector, location) {
    const columnIds = metric.kind === 'ratio_of_sums'
        ? [metric.numeratorColumnId, metric.denominatorColumnId]
        : metric.kind === 'count'
            ? metric.columnId
                ? [metric.columnId]
                : []
            : [metric.columnId];
    for (const columnId of columnIds) {
        const column = findColumn(dataset, columnId);
        if (!column) {
            collector.add('UNKNOWN_COLUMN_REFERENCE', `Widget references column "${columnId}", which is not in the dataset.`, { ...location, columnId });
            continue;
        }
        if (metricRequiresNumeric(metric) && !isNumericColumn(column)) {
            collector.add('UNSUPPORTED_AGGREGATION_FOR_TYPE', `Aggregation "${metric.kind}" needs a number or integer column, but "${column.label}" is of type ${column.type}.`, { ...location, columnId });
        }
    }
}
function validateWidget(widget, dataset, collector, index) {
    const at = (path) => ({ widgetId: widget.id, path: `widgets.${index}.${path}` });
    switch (widget.type) {
        case 'kpi':
            validateMetricAgainstDataset(widget.metric, dataset, collector, at('metric'));
            break;
        case 'chart': {
            if (!findColumn(dataset, widget.dimensionColumnId)) {
                collector.add('UNKNOWN_COLUMN_REFERENCE', `Chart groups by column "${widget.dimensionColumnId}", which is not in the dataset.`, { ...at('dimensionColumnId'), columnId: widget.dimensionColumnId });
            }
            const seriesIds = new Set();
            widget.series.forEach((series, seriesIndex) => {
                if (seriesIds.has(series.id)) {
                    collector.add('REPORT_DEFINITION_INVALID', `Chart has two series with id "${series.id}".`, at(`series.${seriesIndex}.id`));
                }
                seriesIds.add(series.id);
                validateMetricAgainstDataset(series.metric, dataset, collector, at(`series.${seriesIndex}.metric`));
            });
            break;
        }
        case 'table': {
            widget.columns.forEach((column, columnIndex) => {
                if (!findColumn(dataset, column.columnId)) {
                    collector.add('UNKNOWN_COLUMN_REFERENCE', `Table column "${column.columnId}" is not in the dataset.`, { ...at(`columns.${columnIndex}`), columnId: column.columnId });
                }
            });
            if (widget.sort && !findColumn(dataset, widget.sort.columnId)) {
                collector.add('UNKNOWN_COLUMN_REFERENCE', `Table sorts by column "${widget.sort.columnId}", which is not in the dataset.`, { ...at('sort.columnId'), columnId: widget.sort.columnId });
            }
            (widget.groupBy ?? []).forEach((columnId, groupIndex) => {
                if (!findColumn(dataset, columnId)) {
                    collector.add('UNKNOWN_COLUMN_REFERENCE', `Table groups by column "${columnId}", which is not in the dataset.`, { ...at(`groupBy.${groupIndex}`), columnId });
                }
            });
            widget.columns.forEach((column, columnIndex) => {
                if (column.rollup && column.rollup.kind !== 'none') {
                    validateMetricAgainstDataset(column.rollup, dataset, collector, at(`columns.${columnIndex}.rollup`));
                }
            });
            break;
        }
        case 'text':
            break;
    }
}
/**
 * Validates a complete report definition against a dataset schema.
 *
 * Always called on the *resulting* definition of a mutation, never on the patch,
 * so a sequence of individually plausible edits cannot land the report in a
 * state that does not render.
 */
export function validateDefinition(input, dataset) {
    const parsed = ReportDefinitionSchema.safeParse(input);
    if (!parsed.success) {
        throw new ReportPadError('REPORT_DEFINITION_INVALID', 'The report definition is not valid.', {
            issues: zodIssues(parsed.error, 'REPORT_DEFINITION_INVALID')
        });
    }
    const definition = parsed.data;
    const collector = new IssueCollector();
    // Over-deep expansion is a display preference, not a correctness error: clamp
    // it rather than rejecting the whole definition over it. Only clamp when the
    // table is actually grouped — an ungrouped table's defaultExpandedDepth is
    // inert until groupBy is set, and this is the resulting definition of every
    // mutation, so it gets persisted. Rewriting it to 0 here would mean a later
    // update_widget that only adds groupBy switches grouping on fully collapsed
    // instead of at the spec's documented default of 1, and would silently
    // rewrite a field the caller never touched.
    for (const widget of definition.widgets) {
        if (widget.type === 'table') {
            const levels = widget.groupBy?.length ?? 0;
            if (levels > 0 && widget.defaultExpandedDepth > levels) {
                widget.defaultExpandedDepth = levels;
            }
        }
    }
    if (definition.widgets.length > LIMITS.maxWidgetsPerReport) {
        collector.add('LIMIT_EXCEEDED', `A report may contain at most ${LIMITS.maxWidgetsPerReport} widgets; this one has ${definition.widgets.length}.`);
    }
    const widgetIds = new Set();
    definition.widgets.forEach((widget, index) => {
        if (widgetIds.has(widget.id)) {
            collector.add('DUPLICATE_WIDGET_ID', `Two widgets share the id "${widget.id}".`, {
                widgetId: widget.id,
                path: `widgets.${index}.id`
            });
        }
        widgetIds.add(widget.id);
        validateWidget(widget, dataset, collector, index);
    });
    const layoutSeen = new Set();
    definition.layout.items.forEach((item, index) => {
        if (!widgetIds.has(item.widgetId)) {
            collector.add('UNKNOWN_WIDGET_REFERENCE', `Layout places unknown widget "${item.widgetId}".`, {
                widgetId: item.widgetId,
                path: `layout.items.${index}`
            });
        }
        if (layoutSeen.has(item.widgetId)) {
            collector.add('LAYOUT_INVALID', `Widget "${item.widgetId}" is placed twice in the layout.`, {
                widgetId: item.widgetId,
                path: `layout.items.${index}`
            });
        }
        layoutSeen.add(item.widgetId);
        if (item.x + item.w > definition.layout.columns) {
            collector.add('LAYOUT_INVALID', `Widget "${item.widgetId}" spans past the ${definition.layout.columns}-column grid (x=${item.x}, w=${item.w}).`, { widgetId: item.widgetId, path: `layout.items.${index}` });
        }
    });
    for (const widget of definition.widgets) {
        if (!layoutSeen.has(widget.id)) {
            collector.add('LAYOUT_INVALID', `Widget "${widget.id}" has no layout entry.`, { widgetId: widget.id });
        }
    }
    for (const id of [...definition.print.order, ...definition.print.hidden]) {
        if (!widgetIds.has(id)) {
            collector.add('UNKNOWN_WIDGET_REFERENCE', `Print options reference unknown widget "${id}".`, { widgetId: id });
        }
    }
    collector.throwIfFailed('REPORT_DEFINITION_INVALID', 'The report definition is not valid');
    return definition;
}
/**
 * Can `definition` be rendered against a snapshot validated for `dataset`?
 *
 * This is the gate that protects a refresh. The check is intentionally about
 * the *bindings a report actually uses*, not about schema equality: adding a
 * column to a dataset must not invalidate existing reports, while removing or
 * retyping a bound column must.
 */
export function checkSnapshotCompatibility(definition, dataset, snapshotFingerprint) {
    const collector = new IssueCollector();
    const required = definitionColumnIds(definition);
    for (const columnId of required) {
        if (!findColumn(dataset, columnId)) {
            const widget = definition.widgets.find((w) => widgetColumnIds(w).includes(columnId));
            collector.add('SNAPSHOT_INCOMPATIBLE', `This snapshot has no column "${columnId}", which widget "${widget?.title ?? columnId}" depends on. Submit a snapshot that includes it, or edit the widget first.`, widget ? { widgetId: widget.id, columnId } : { columnId });
        }
    }
    definition.widgets.forEach((widget, index) => {
        validateWidget(widget, dataset, collector, index);
    });
    // A snapshot is stamped with the fingerprint of the schema it was validated
    // against. If the dataset's own fingerprint has since moved, the rows in that
    // snapshot were checked against different rules and cannot be trusted here.
    const current = datasetSchemaFingerprint(dataset);
    if (snapshotFingerprint !== current) {
        collector.add('SNAPSHOT_INCOMPATIBLE', `This snapshot was validated against a different dataset schema (${snapshotFingerprint}) than the one the report uses (${current}). Submit a new snapshot against the current schema.`);
    }
    const typedIssues = collector.issues.map((issue) => ({ ...issue, code: 'SNAPSHOT_INCOMPATIBLE' }));
    return { compatible: typedIssues.length === 0, issues: typedIssues };
}
/** Throws a single structured error describing every reason a refresh was
 *  refused. The caller's active snapshot is left untouched. */
export function assertSnapshotCompatible(definition, dataset, snapshotFingerprint) {
    const result = checkSnapshotCompatibility(definition, dataset, snapshotFingerprint);
    if (result.compatible)
        return;
    const first = result.issues[0];
    throw new ReportPadError('SNAPSHOT_INCOMPATIBLE', result.issues.length === 1 ? first.message : `${first.message} (+${result.issues.length - 1} more)`, { issues: result.issues, details: { snapshotFingerprint } });
}
// ---------------------------------------------------------------------------
export function zodIssues(error, code) {
    return error.issues.map((issue) => ({
        code,
        message: `${issue.path.length > 0 ? `${issue.path.join('.')}: ` : ''}${issue.message}`,
        location: { path: issue.path.join('.') }
    }));
}
/** Parses untrusted input with a zod schema, raising a ReportPadError. */
export function parseOrThrow(schema, input, code = 'INVALID_REQUEST', summary = 'Request is not valid') {
    const parsed = schema.safeParse(input);
    if (parsed.success)
        return parsed.data;
    const issues = zodIssues(parsed.error, code);
    throw new ReportPadError(code, `${summary}: ${issues[0]?.message ?? 'unknown validation error'}`, { issues });
}
//# sourceMappingURL=validation.js.map